import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { ChartCard, EmptyState } from '@/components/ChartCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { formatNumber, formatPct } from '@/lib/format';
import { ExternalLink, Globe, Zap } from 'lucide-react';

function ownerStyle(owner) {
  if (owner === 'brand') return 'border-emerald-500/40 text-emerald-700 bg-emerald-500/10';
  if (owner === 'third_party' || !owner) return 'text-muted-foreground';
  return 'border-amber-500/40 text-amber-700 bg-amber-500/10'; // any competitor label
}
function ownerLabel(owner) {
  if (owner === 'brand') return 'Brand';
  if (owner === 'third_party' || !owner) return '3rd party';
  return owner; // e.g. "Evernote"
}

export default function CitationsTab({ project }) {
  const [domains, setDomains] = useState([]);
  const [urls, setUrls] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [byMode, setByMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ownerFilter, setOwnerFilter] = useState('all');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/projects/${project.id}/analytics/citations`);
        setDomains(r.data.domains || []);
        setUrls(r.data.urls || []);
        setBreakdown(r.data.breakdown || null);
        setByMode(r.data.by_mode || null);
      } finally {
        setLoading(false);
      }
    })();
  }, [project.id]);

  const nativeShare = useMemo(() => {
    if (!byMode) return 0;
    const total = (byMode.native || 0) + (byMode.emergent || 0);
    return total ? (byMode.native / total) : 0;
  }, [byMode]);

  const filteredDomains = useMemo(() => {
    if (ownerFilter === 'all') return domains;
    if (ownerFilter === 'competitor') {
      return domains.filter((d) => d.owner && d.owner !== 'brand' && d.owner !== 'third_party');
    }
    return domains.filter((d) => (d.owner || 'third_party') === ownerFilter);
  }, [domains, ownerFilter]);

  const filteredUrls = useMemo(() => {
    if (ownerFilter === 'all') return urls;
    if (ownerFilter === 'competitor') {
      return urls.filter((u) => u.owner && u.owner !== 'brand' && u.owner !== 'third_party');
    }
    return urls.filter((u) => (u.owner || 'third_party') === ownerFilter);
  }, [urls, ownerFilter]);

  if (loading) return <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-64" />)}</div>;

  return (
    <div className="space-y-4">
      {/* Summary bar --------------------------------------------------- */}
      {breakdown && breakdown.total > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border p-3 bg-secondary/30" data-testid="citations-summary-bar">
          <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium mr-1">Attribution</div>
          <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 bg-emerald-500/10">
            Brand · {formatNumber(breakdown.brand)}
          </Badge>
          <Badge variant="outline" className="border-amber-500/40 text-amber-700 bg-amber-500/10">
            Competitors · {formatNumber(breakdown.competitor)}
          </Badge>
          <Badge variant="outline" className="text-muted-foreground">
            Third-party · {formatNumber(breakdown.third_party)}
          </Badge>
          <div className="w-px h-4 bg-border mx-1" />
          <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium mr-1">Freshness</div>
          <Badge
            variant="outline"
            className="border-emerald-500/40 text-emerald-700 bg-emerald-500/10 gap-1"
            title="Citations captured via provider-native web_search / grounding (real-time)"
            data-testid="citations-live-share"
          >
            <Zap className="h-3 w-3" /> Live web · {formatPct(nativeShare)}
          </Badge>
          <Badge variant="outline" className="text-muted-foreground" title="Citations from base-model answers (add provider API keys for live grounding)">
            Base model · {formatNumber(byMode?.emergent || 0)}
          </Badge>
        </div>
      )}

      {/* Filter chips -------------------------------------------------- */}
      {breakdown && breakdown.total > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="citations-filter-bar">
          {['all', 'brand', 'competitor', 'third_party'].map((f) => (
            <button
              key={f}
              onClick={() => setOwnerFilter(f)}
              data-testid={`citations-filter-${f}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                ownerFilter === f
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-transparent hover:bg-secondary/50'
              }`}
            >
              {f === 'all' && 'All'}
              {f === 'brand' && 'Brand only'}
              {f === 'competitor' && 'Competitors only'}
              {f === 'third_party' && 'Third-party only'}
            </button>
          ))}
        </div>
      )}

      {domains.length === 0 && urls.length === 0 ? (
        <ChartCard title="Citations" subtitle="URLs and domains cited by AI answers">
          <EmptyState title="No citations yet" description="Run a scan — we'll extract cited URLs from AI responses." />
        </ChartCard>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Top cited domains" subtitle={`${formatNumber(filteredDomains.length)} of ${formatNumber(domains.length)} shown`}>
            <div className="space-y-1">
              {filteredDomains.slice(0, 20).map((d) => (
                <div key={d.domain} className="flex items-center justify-between rounded-md border px-3 py-2" data-testid={`citation-domain-${d.domain}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <Badge variant="outline" className={`text-[10px] shrink-0 ${ownerStyle(d.owner)}`}>{ownerLabel(d.owner)}</Badge>
                    <div className="text-sm truncate">{d.domain}</div>
                  </div>
                  <div className="text-xs tabular font-medium">{formatNumber(d.count)}</div>
                </div>
              ))}
              {filteredDomains.length === 0 && (
                <div className="text-xs text-muted-foreground py-6 text-center">No domains match this filter.</div>
              )}
            </div>
          </ChartCard>

          <ChartCard title="Top cited URLs" subtitle={`${formatNumber(filteredUrls.length)} of ${formatNumber(urls.length)} shown`}>
            <div className="space-y-1 max-h-[500px] overflow-y-auto">
              {filteredUrls.slice(0, 100).map((u, i) => (
                <a key={i} href={u.url} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-md border px-3 py-2 hover:bg-secondary/40 transition-colors" data-testid={`citation-url-${i}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <Badge variant="outline" className={`text-[10px] shrink-0 ${ownerStyle(u.owner)}`}>{ownerLabel(u.owner)}</Badge>
                    <div className="text-xs truncate">{u.url}</div>
                  </div>
                  <div className="text-xs tabular font-medium ml-2">{u.count}</div>
                </a>
              ))}
              {filteredUrls.length === 0 && (
                <div className="text-xs text-muted-foreground py-6 text-center">No URLs match this filter.</div>
              )}
            </div>
          </ChartCard>
        </div>
      )}
    </div>
  );
}
